// cognitoSRPService.ts - Frontend SRP authentication service
// This service handles USER_SRP_AUTH authentication directly with AWS Cognito
// It does NOT go through the backend, ensuring passwords never leave the browser in clear text
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
// Get Cognito configuration from environment variables
// These should be set in your .env file or environment
const USER_POOL_ID = process.env.COGNITO_USER_POOL_ID || '';
const CLIENT_ID = process.env.COGNITO_CLIENT_ID || '';
// Validate that required configuration is present
if (!USER_POOL_ID || !CLIENT_ID) {
    console.warn('⚠️ [Cognito SRP Service] Missing COGNITO_USER_POOL_ID or COGNITO_CLIENT_ID in environment variables');
}
// Create Cognito User Pool instance
// This is used to create CognitoUser objects for authentication
const userPool = new CognitoUserPool({
    UserPoolId: USER_POOL_ID,
    ClientId: CLIENT_ID
});
/**
 * Check if a string is a valid email address
 */
function isValidEmail(str) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(str);
}
/**
 * Authenticate user with SRP (Secure Remote Password) protocol
 *
 * This method uses USER_SRP_AUTH which:
 * - Never sends the password in clear text (even to the backend)
 * - Uses cryptographic challenge-response protocol
 * - Is the most secure method for client-side authentication
 *
 * Note: This method does NOT support temporary passwords (NEW_PASSWORD_REQUIRED challenge).
 * For temporary passwords, use the backend's ADMIN_USER_PASSWORD_AUTH flow.
 *
 * Supports both username and email login if the User Pool is configured to allow email as username.
 *
 * @param usernameOrEmail - User's username or email address
 * @param password - User's password (permanent password only)
 * @returns Promise with authentication result containing tokens or error
 */
export function authenticateWithSRP(usernameOrEmail, password) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve) => {
            // Determine if input is email or username
            // If it's an email and User Pool supports email login, use it directly
            // Otherwise, use as username
            const identifier = usernameOrEmail.trim();
            const isEmail = isValidEmail(identifier);
            console.log('🔍 [Cognito SRP Service] Attempting authentication with:', {
                identifier,
                isEmail,
                userPoolId: USER_POOL_ID,
                clientId: CLIENT_ID
            });
            // Create CognitoUser object for the specified identifier
            // Cognito will handle whether it's a username or email based on User Pool configuration
            const cognitoUser = new CognitoUser({
                Username: identifier,
                Pool: userPool
            });
            // Create AuthenticationDetails with user credentials
            const authenticationDetails = new AuthenticationDetails({
                Username: identifier,
                Password: password
            });
            // Initiate SRP authentication
            // This will perform the SRP challenge-response protocol with Cognito
            cognitoUser.authenticateUser(authenticationDetails, {
                // Success callback - authentication completed successfully
                onSuccess: (result) => {
                    console.log('✅ [Cognito SRP Service] Authentication successful with SRP');
                    // Extract tokens from authentication result
                    const tokens = {
                        AccessToken: result.getAccessToken().getJwtToken(),
                        IdToken: result.getIdToken().getJwtToken(),
                        RefreshToken: result.getRefreshToken().getToken()
                    };
                    resolve({
                        success: true,
                        tokens
                    });
                },
                // Failure callback - authentication failed
                onFailure: (err) => {
                    console.error('❌ [Cognito SRP Service] SRP authentication failed:', err);
                    console.error('❌ [Cognito SRP Service] Error details:', {
                        name: err.name,
                        code: err.code,
                        message: err.message,
                        stack: err.stack
                    });
                    // Check if error indicates temporary password is required
                    // Some Cognito errors indicate that user needs to use temporary password flow
                    const errorMessage = err.message || err.toString();
                    const errorCode = err.code || err.name || '';
                    const needsTemporaryPassword = errorMessage.includes('NEW_PASSWORD_REQUIRED') ||
                        errorMessage.includes('PasswordResetRequiredException') ||
                        errorMessage.includes('temporary password');
                    // Provide more specific error messages
                    let userFriendlyError = errorMessage;
                    if (errorMessage.includes('User does not exist') || errorMessage.includes('UserNotFoundException') || errorCode === 'UserNotFoundException') {
                        userFriendlyError = 'No account found with this username or email. Please check your credentials or create a new account.';
                    }
                    else if (errorMessage.includes('Incorrect username or password') || errorMessage.includes('NotAuthorizedException') || errorCode === 'NotAuthorizedException') {
                        userFriendlyError = 'Incorrect username/email or password. Please try again.';
                    }
                    resolve({
                        success: false,
                        error: userFriendlyError,
                        errorCode: errorCode, // Return original error code for programmatic detection
                        needsTemporaryPassword
                    });
                },
                // Challenge callback - additional authentication step required
                // This is called when Cognito requires additional information (e.g., MFA)
                // For NEW_PASSWORD_REQUIRED, we handle it as a failure and use backend flow
                newPasswordRequired: (userAttributes, requiredAttributes) => {
                    console.log('⚠️ [Cognito SRP Service] NEW_PASSWORD_REQUIRED challenge detected');
                    console.log('⚠️ [Cognito SRP Service] SRP does not support temporary passwords, use backend flow');
                    // SRP does not support NEW_PASSWORD_REQUIRED challenge
                    // User must use backend ADMIN_USER_PASSWORD_AUTH flow instead
                    resolve({
                        success: false,
                        error: 'NEW_PASSWORD_REQUIRED - Temporary password detected. Please use temporary password login.',
                        needsTemporaryPassword: true
                    });
                }
            });
        });
    });
}
/**
 * Get current authenticated user from Cognito
 *
 * @returns Promise with user information or null if not authenticated
 */
export function getCurrentCognitoUser() {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve) => {
            // Get current user from the user pool
            const cognitoUser = userPool.getCurrentUser();
            if (!cognitoUser) {
                resolve(null);
                return;
            }
            // Verify that the user's session is still valid
            cognitoUser.getSession((err, session) => {
                if (err || !session.isValid()) {
                    resolve(null);
                    return;
                }
                resolve(cognitoUser);
            });
        });
    });
}
/**
 * Sign out current user from Cognito
 *
 * @returns Promise that resolves when sign out is complete
 */
export function signOutCognitoUser() {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve) => {
            const cognitoUser = userPool.getCurrentUser();
            if (cognitoUser) {
                cognitoUser.signOut(() => {
                    console.log('✅ [Cognito SRP Service] User signed out from Cognito');
                    resolve();
                });
            }
            else {
                resolve();
            }
        });
    });
}
